import { Inject, Injectable, Optional } from '@angular/core';
import { combineLatest, isObservable, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import {
  CX_DESIGN_VERSION_TOKEN,
  CX_RTL_TOKEN,
  CxUiDesignVersion,
  CxUiRTL,
} from './cx-design-version/cx-design-version.config';
import {
  CX_INPUT_DATE_FIRST_DAY_OF_WEEK_TOKEN,
  CX_INPUT_DATE_FORMAT_TOKEN,
  CX_INPUT_DATE_LANGUAGE_TOKEN,
  CxUiInputDateDateFormat,
  CxUiInputDateFirstDayOfWeek,
  CxUiInputDateLanguage,
  CX_INPUT_DATE_CONTROL_BUTTONS_TOKEN,
  CxUiInputDateControlButtons,
  CX_INPUT_DATE_HIDE_RESET_BUTTON_TOKEN,
  CxUiInputDateHideResetButton,
  cxUiInputDateLabelApply,
  CX_INPUT_DATE_LABEL_APPLY_TOKEN,
  CX_INPUT_DATE_LABEL_CANCEL_TOKEN,
  cxUiInputDateLabelCancel,
} from './cx-input-date/cx-input-date.config';
import {
  CX_INPUT_TIME_FORMAT_TOKEN,
  CxUiInputTimeFormat,
  CX_INPUT_TIME_CONTROL_BUTTONS_TOKEN,
  CxUiInputTimeControlButtons,
  CxUiInputTimeHideResetButton,
  CX_INPUT_TIME_HIDE_RESET_BUTTON_TOKEN,
  CxUiInputTimeLabelApply,
  CX_INPUT_TIME_LABEL_APPLY_TOKEN,
  CxUiInputTimeLabelCancel,
  CX_INPUT_TIME_LABEL_CANCEL_TOKEN,
} from './cx-input-time/cx-input-time.config';

@Injectable()
export class CxUiConfigService {
  public config$ = combineLatest([
    this.getValue(this.designVersionToken),
    this.getValue(this.rtlToken),
    this.getValue(this.dateFormatToken),
    this.getValue(this.dateLanguageToken),
    this.getValue(this.dateFirstDayOfWeekToken),
    this.getValue(this.timeFormatToken),
    this.getValue(this.dateControlButtonsToken),
    this.getValue(this.timeControlButtonsToken),
    this.getValue(this.dateHideResetButton),
    this.getValue(this.timeHideResetButton),
    this.getValue(this.dateLabelApply),
    this.getValue(this.dateLabelCancel),
    this.getValue(this.timeLabelApply),
    this.getValue(this.timeLabelCancel),
  ]).pipe(
    map(
      ([
        designVersion,
        rtl,
        dateFormat,
        dateLanguage,
        dateFirstDayOfWeek,
        timeFormat,
        dateControlButtons,
        timeControlButtons,
        dateHideResetButton,
        timeHideResetButton,
        dateLabelApply,
        dateLabelCancel,
        timeLabelApply,
        timeLabelCancel,
      ]) => {
        return {
          designVersion,
          rtl,
          dateFormat,
          dateLanguage,
          dateFirstDayOfWeek,
          timeFormat,
          dateControlButtons,
          timeControlButtons,
          dateHideResetButton,
          timeHideResetButton,
          dateLabelApply,
          dateLabelCancel,
          timeLabelApply,
          timeLabelCancel,
        } as CxUiConfig;
      },
    ),
    shareReplay(),
  );

  constructor(
    @Optional()
    @Inject(CX_DESIGN_VERSION_TOKEN)
    private designVersionToken?: CxUiDesignVersion | Observable<CxUiDesignVersion>,

    @Optional()
    @Inject(CX_RTL_TOKEN)
    private rtlToken?: CxUiRTL | Observable<CxUiRTL>,

    @Optional()
    @Inject(CX_INPUT_DATE_FORMAT_TOKEN)
    private dateFormatToken?: CxUiInputDateDateFormat | Observable<CxUiInputDateDateFormat>,

    @Optional()
    @Inject(CX_INPUT_DATE_LANGUAGE_TOKEN)
    private dateLanguageToken?: CxUiInputDateLanguage | Observable<CxUiInputDateLanguage>,

    @Optional()
    @Inject(CX_INPUT_DATE_FIRST_DAY_OF_WEEK_TOKEN)
    private dateFirstDayOfWeekToken?: CxUiInputDateFirstDayOfWeek | Observable<CxUiInputDateFirstDayOfWeek>,

    @Optional()
    @Inject(CX_INPUT_TIME_FORMAT_TOKEN)
    private timeFormatToken?: CxUiInputTimeFormat | Observable<CxUiInputTimeFormat>,

    @Optional()
    @Inject(CX_INPUT_DATE_CONTROL_BUTTONS_TOKEN)
    private dateControlButtonsToken?: CxUiInputDateControlButtons | Observable<CxUiInputDateControlButtons>,

    @Optional()
    @Inject(CX_INPUT_TIME_CONTROL_BUTTONS_TOKEN)
    private timeControlButtonsToken?: CxUiInputTimeControlButtons | Observable<CxUiInputTimeControlButtons>,

    @Optional()
    @Inject(CX_INPUT_DATE_HIDE_RESET_BUTTON_TOKEN)
    private dateHideResetButton?: CxUiInputDateHideResetButton | Observable<CxUiInputDateHideResetButton>,

    @Optional()
    @Inject(CX_INPUT_TIME_HIDE_RESET_BUTTON_TOKEN)
    private timeHideResetButton?: CxUiInputTimeHideResetButton | Observable<CxUiInputTimeHideResetButton>,

    @Optional()
    @Inject(CX_INPUT_DATE_LABEL_APPLY_TOKEN)
    private dateLabelApply?: cxUiInputDateLabelApply | Observable<cxUiInputDateLabelApply>,

    @Optional()
    @Inject(CX_INPUT_DATE_LABEL_CANCEL_TOKEN)
    private dateLabelCancel?: cxUiInputDateLabelCancel | Observable<cxUiInputDateLabelCancel>,

    @Optional()
    @Inject(CX_INPUT_TIME_LABEL_APPLY_TOKEN)
    private timeLabelApply?: CxUiInputTimeLabelApply | Observable<CxUiInputTimeLabelApply>,

    @Optional()
    @Inject(CX_INPUT_TIME_LABEL_CANCEL_TOKEN)
    private timeLabelCancel?: CxUiInputTimeLabelCancel | Observable<CxUiInputTimeLabelCancel>,
  ) {}

  public getValue<T>(value: T) {
    if (isObservable(value)) {
      return value;
    } else {
      return of(value);
    }
  }

  public selectValueOf(property: keyof CxUiConfig) {
    return this.config$.pipe(
      map(config => config[property]),
      distinctUntilChanged(),
    );
  }
}

export interface CxUiConfig {
  // Web components general properties like design-version or rtl
  designVersion?: CxUiDesignVersion;
  rtl?: CxUiRTL;

  // CwcInputDate properties
  dateFormat?: CxUiInputDateDateFormat;
  dateLanguage?: CxUiInputDateLanguage;
  dateFirstDayOfWeek?: CxUiInputDateFirstDayOfWeek;
  dateControlButtons?: CxUiInputDateControlButtons;
  dateHideResetButton?: CxUiInputDateHideResetButton;
  dateLabelApply?: cxUiInputDateLabelApply;
  dateLabelCancel?: cxUiInputDateLabelCancel;

  // CwcInputTime properties
  timeFormat?: CxUiInputTimeFormat;
  timeControlButtons?: CxUiInputTimeControlButtons;
  timeHideResetButton?: CxUiInputTimeHideResetButton;
  timeLabelApply?: CxUiInputTimeLabelApply;
  timeLabelCancel?: CxUiInputTimeLabelCancel;
}
